import { useEffect } from 'react'
import { storeValue } from 'shared/utils/localStrorageUtils'
import {
    getFromLocalStorage,
    updatePersistentConfigs,
} from 'shared/utils/workspaceUtils'

/**
 * Synchronizes workspace toggle state based on firm creation date and user preferences.
 * This hook ensures consistent behavior across components that need to manage workspace toggling.
 *
 * @param shouldHideToggle - Whether the toggle should be hidden (newer firms)
 * @param userId - Current user ID for localStorage
 * @param setStateValue - Function to set state values
 * @param switchWorkspace - Function to switch workspace
 */
export const useWorkspaceToggleSync = (
    shouldHideToggle: boolean,
    userId: string | undefined,
    setStateValue: (key: string, value: boolean) => void,
    switchWorkspace: (enabled: boolean) => void
) => {
    useEffect(() => {
        if (shouldHideToggle) {
            // Persist to ALL localStorage locations to prevent old preference from overriding
            updatePersistentConfigs(
                userId,
                'Global',
                'betaWorkspaceToggleChecked',
                true
            )
            storeValue('enableBetaWorkspace', 'true')
            // Force new workspace for firms created after cutoff date
            setStateValue('showHideStates.betaWorkspace', true)
            switchWorkspace(true)
        } else {
            // Check user preference from localStorage
            const isWorkspace2 = getFromLocalStorage(
                userId ?? '',
                'Global',
                'betaWorkspaceToggleChecked'
            )
            // Default to false (classic workspace) if no preference stored (null)
            // Old firms should start with classic workspace and can opt into new workspace
            const betaWorkspaceEnabled =
                isWorkspace2 === null ? false : isWorkspace2
            setStateValue('showHideStates.betaWorkspace', betaWorkspaceEnabled)
            switchWorkspace(betaWorkspaceEnabled)
        }
    }, [shouldHideToggle, userId, setStateValue, switchWorkspace])
}
